/**
 * cellWorkflow.js — reading and writing a cell's workflow, which is an EMBEDDED COPY.
 *
 * `cell.props.cell_workflow` (and a mission's default) holds the body itself — `{ alias, nodes, edges }`
 * — not a reference to a palette entry. That is deliberate and load-bearing: editing a library entry
 * must not reshape a cell that is already running, so the orchestrator interprets the cell's own copy
 * and the copy is frozen the first time the cell enters `working`. An alias would make every in-flight
 * cell's graph shift under it the moment somebody tidied the palette.
 *
 * Two consequences this module exists to absorb:
 *
 *  - **Anything rendering the value must render `.alias`**, never the value. Passing the body to JSX
 *    is what produced "Objects are not valid as a React child (found: object with keys
 *    {alias, edges, description, nodes})" — a whole widget taken down by one interpolation.
 *  - **Anything picking a workflow must write the body**, resolved from the palette at pick time.
 *
 * Legacy cells authored before the embed decision carry a bare alias string. Reading tolerates that;
 * writing never produces it. That is a one-line concession instead of a data migration.
 */

/** The alias of a workflow value — the embedded body's `alias`, or a legacy bare string. */
export function workflowAlias(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return typeof value.alias === 'string' ? value.alias : '';
}

/** True when the value carries an interpretable step graph rather than just an alias. */
export function isWorkflowBody(value) {
  return !!value && typeof value === 'object' && Array.isArray(value.nodes);
}

/**
 * The step graph to interpret for a cell.
 *
 * The embedded copy wins — always, and even when the palette has an entry of the same alias, because
 * the copy is what the orchestrator walks. The palette is consulted only to resolve a legacy alias.
 *
 * @param {Object|string|null} value - cell.props.cell_workflow
 * @param {Array} workflows - the `app/missions/cell_workflows` palette
 * @returns {Object|null}
 */
export function workflowBody(value, workflows) {
  if (isWorkflowBody(value)) return value;
  const alias = workflowAlias(value);
  if (!alias || !Array.isArray(workflows)) return null;
  return workflows.find(entry => entry && entry.alias === alias) || null;
}

/**
 * The value to WRITE when someone picks `alias` from the palette: the body, copied.
 * Returns null for a cleared selection, and null for an alias the palette does not have — writing a
 * bare alias would recreate the reference semantics this embed exists to avoid.
 */
export function workflowSelection(alias, workflows) {
  if (!alias || !Array.isArray(workflows)) return null;
  const entry = workflows.find(candidate => candidate && candidate.alias === alias);
  return entry ? { ...entry } : null;
}


/** The five fields a step's agent block carries, beside the `alias` it was resolved from. */
const AGENT_FIELDS = ['type', 'executable', 'model', 'effort', 'permissions'];

/** Whether `entry` carries every field a block needs — the one predicate agentSelection AND the
 * menu filter below it both answer from, so there is exactly one place that defines "complete". */
function isCompleteAgentEntry(entry) {
  return !!entry && AGENT_FIELDS.every(field => !!entry[field]);
}

/**
 * The block to WRITE when someone picks `alias` from the palette: the entry's five fields, plus the
 * alias that names them.
 *
 * A copy rather than a reference, for the reason the workflow body is a copy: the orchestrator freezes
 * what the cell holds, and a step naming a palette entry that is later edited would have its harness
 * changed under it. `description` is palette prose for a human choosing, and is not part of the block.
 *
 * Null for an alias the palette does not have, or one that resolves to an entry missing a required
 * field — writing a half-built block would produce a step that refuses to dispatch, and returning null
 * rather than a partial object is what lets a caller guard the write on it.
 */
export function agentSelection(alias, agents) {
  if (!alias || !Array.isArray(agents)) return null;
  const entry = agents.find(candidate => candidate && candidate.alias === alias);
  if (!isCompleteAgentEntry(entry)) return null;
  const block = { alias: entry.alias };
  AGENT_FIELDS.forEach(field => { block[field] = entry[field]; });
  return block;
}

/**
 * The palette entries `agentSelection` can actually resolve to a legal block — i.e. every one that
 * carries all five required fields. A picker's menu built from this can never offer a choice that
 * would silently resolve to null: an entry missing a field (a malformed palette, an entry still being
 * authored) never appears to be pickable in the first place.
 */
export function resolvableAgents(agents) {
  return (Array.isArray(agents) ? agents : []).filter(isCompleteAgentEntry);
}


/**
 * The harness vocabulary — `app/missions/cell_workflows`'s `harnesses` key. Where `agents` holds
 * already-assembled blocks to pick from, this holds the parts one is BUILT out of: a harness names
 * its executable and the models and efforts it can run.
 *
 * Same rule as resolvableAgents, one level up: an entry a slider cannot be built from never reaches
 * one. `models`/`efforts` must be non-empty because a scale with no stops is not a choice, and
 * `executable` must be there because it is the one field no slider can supply — the person moving
 * sliders never sees it, so a harness missing it would build a block that refuses to dispatch.
 */
function isResolvableHarness(entry) {
  return !!entry
    && typeof entry.alias === 'string' && !!entry.alias
    && typeof entry.executable === 'string' && !!entry.executable
    && Array.isArray(entry.models) && entry.models.length > 0
    && Array.isArray(entry.efforts) && entry.efforts.length > 0;
}

/** The harness entries usable as a slider vocabulary. Mirrors resolvableAgents' contract. */
export function resolvableHarnesses(harnesses) {
  return (Array.isArray(harnesses) ? harnesses : []).filter(isResolvableHarness);
}

/**
 * The permission ladder, least to most. The one vocabulary that is NOT per-harness — every harness
 * understands the same three — so it lives here rather than on a palette entry, and the popover
 * reads it from here rather than holding a fourth copy of the list.
 */
export const AGENT_PERMISSIONS = Object.freeze(['approval', 'guarded', 'autonomous']);

/**
 * Short labels for a scale's stops: whatever every value shares, dropped.
 *
 * `claude-haiku-4-5 | claude-sonnet-5 | claude-opus-5` under a slider labelled Model reads as three
 * copies of the word `claude` and one bit of information each; the shared head is exactly the part
 * the slider's own name already said. Split on `-` so a segment is never cut in half.
 *
 * **Never consumes a whole value**, and that backs off all-or-nothing rather than by one segment:
 * where the shared head IS one of the values (`x-a` beside `x-a-b`), there is no honest short form
 * of the shorter one, so the list is left exactly as it came. Labels that came back empty would be
 * a scale of blank stops.
 */
export function tickLabels(values) {
  const list = Array.isArray(values) ? values.map(v => String(v)) : [];
  if (list.length < 2) return list;
  const parts = list.map(v => v.split('-'));
  // Declared outside the loop below and taking its index as an argument: a closure over a variable
  // the loop reassigns is what `no-loop-func` is about, and this is the same rule without one.
  const agrees = index => parts.every(p => p[index] === parts[0][index]);
  const shortest = Math.min(...parts.map(p => p.length));
  let common = 0;
  while (common < shortest && agrees(common)) common += 1;
  if (common >= shortest) return list;
  return parts.map(p => p.slice(common).join('-'));
}

/**
 * Where a tick strip should scroll to so `labelLeft`/`labelWidth` sits centred in the viewport.
 *
 * Takes every number as an argument rather than reading the DOM itself, and that is the point:
 * jsdom does no layout, so every box it reports is zero width, and a function that measured its own
 * elements could not be tested at all. This is the one piece of the strip that genuinely is
 * arithmetic once the measurements exist, so it is pulled out to where jest can reach it.
 *
 * `labelLeft` is a `getBoundingClientRect()` delta — the label's distance from the viewport's own
 * left edge, as currently rendered — not its offset within the scrolled content. That is why the
 * result adds the delta to the current `scrollLeft` instead of replacing it: the same label reported
 * from an already-scrolled strip has a smaller `labelLeft`, and the two must agree on where it is.
 * Clamped to `[0, contentWidth - viewportWidth]` so a label near either end pulls the strip only as
 * far as it can actually go, and a content narrower than its viewport never scrolls at all.
 */
export function centredScrollLeft({ scrollLeft, viewportWidth, contentWidth, labelLeft, labelWidth }) {
  const delta = labelLeft - (viewportWidth - labelWidth) / 2;
  const max = Math.max(0, contentWidth - viewportWidth);
  return Math.min(Math.max(scrollLeft + delta, 0), max);
}

/**
 * The block to WRITE for an agent built in place: the four slider values, plus the executable the
 * harness carries.
 *
 * **No `alias`, deliberately.** An alias names a palette entry, and this block came from nowhere but
 * the sliders — inventing one would claim a preset that does not exist and would have `agentLabel`
 * report a name nothing can resolve. An alias-less block is legal, is what an API-built one looks
 * like, and is what `agentLabel` already calls `custom`.
 *
 * `type` is the harness's own alias, which is what the orchestrator reads to choose a harness.
 * Null when any part is missing or the harness is not resolvable — the same guard-on-null contract
 * agentSelection has, so a caller can refuse the write rather than half-build a step.
 */
export function customAgentBlock(harness, model, effort, permissions) {
  if (!isResolvableHarness(harness) || !model || !effort || !permissions) return null;
  return { type: harness.alias, executable: harness.executable, model, effort, permissions };
}

/**
 * Where the four sliders sit for a step's EXISTING block — indices, not values, since that is what
 * a range input takes.
 *
 * **Null means "cannot represent this", and is load-bearing.** A block may legally name a model or
 * a harness the palette does not list: it was built through the API, or the palette moved on after
 * it was written. Clamping that to index 0 would leave a slider resting on `haiku` while the step
 * actually runs `opus` — the UI stating something false about what will run. Null lets the popover
 * say so instead.
 */
/**
 * The five-field block a step EFFECTIVELY runs, given what it carries and the palette.
 *
 * A step in DRAFT still carries the palette REFERENCE — `agent: {alias: claude}` — because expansion
 * into the five fields happens at the freeze, on draft exit. That is not a block that cannot be
 * represented; it is one that has not been expanded yet, and it resolves perfectly well. Anything
 * reading a step's agent BEFORE the freeze has to resolve it, or it is reading a reference as though
 * it were a block and concluding the step runs nothing recognisable.
 *
 * An already-expanded block wins over its own alias, and the order matters: a cell frozen against a
 * palette entry that has since been edited runs what it froze, so reporting the palette's CURRENT
 * values for it would state something false about what the next run will do.
 */
export function effectiveAgent(agent, agents) {
  if (!agent || typeof agent !== 'object') return null;
  if (isCompleteAgentEntry(agent)) return agent;
  return agent.alias ? agentSelection(agent.alias, agents) : null;
}

export function agentDraft(agent, harnesses) {
  if (!agent || typeof agent !== 'object') return null;
  const list = resolvableHarnesses(harnesses);
  const harness = list.findIndex(entry => entry.alias === agent.type);
  if (harness < 0) return null;
  const model = list[harness].models.indexOf(agent.model);
  const effort = list[harness].efforts.indexOf(agent.effort);
  const permissions = AGENT_PERMISSIONS.indexOf(agent.permissions);
  if (model < 0 || effort < 0 || permissions < 0) return null;
  return { harness, model, effort, permissions };
}

/**
 * Whether two agent blocks are the same block.
 *
 * The alias counts. Dropping one is exactly what turns a named preset into a custom block — the
 * chip goes from `claude` to `custom` — so a no-op guard that compared only the five fields would
 * swallow that as "no change" and leave the step named after a preset it no longer carries.
 *
 * Compared field by field rather than by serialising: a block read back from the server carries the
 * same fields in whatever order it was written in, and a guard that turned on key order would post
 * a redundant save after every reload.
 */
export function isSameAgent(a, b) {
  if (!a || !b) return false;
  return (a.alias || '') === (b.alias || '') && AGENT_FIELDS.every(field => a[field] === b[field]);
}

/**
 * A copy of `body` with `agent` on the step named `stepId` — or with the `agent` KEY REMOVED when
 * `agent` is `undefined`. The body itself is never mutated.
 *
 * The removal case exists for clearing a step's agent. `workflow_overrides.agent` may still supply
 * one at dispatch, so "no agent block on the step" is a legal, meaningful state the orchestrator
 * reads by the key being ABSENT — writing `agent: null` is a different (illegal) shape and was the
 * bug an earlier round of this feature fixed. Passing `undefined` explicitly is how a caller asks
 * for the key to go away, distinct from every other call here, which passes a real block.
 */
export function withStepAgent(body, stepId, agent) {
  if (!body || !Array.isArray(body.nodes)) return body;
  return {
    ...body,
    nodes: body.nodes.map(node => {
      if (!node || node.id !== stepId) return node;
      if (agent === undefined) {
        const { agent: _dropped, ...rest } = node;
        return rest;
      }
      return { ...node, agent };
    }),
  };
}

/**
 * What a step's chip says: the alias its block was resolved from, or `custom` when it carries none —
 * which is what a block built through the API looks like, and is legal. Null where there is nothing to
 * name, so a caller can decide whether to render a chip at all.
 */
export function agentLabel(node) {
  const agent = node && node.agent;
  if (!agent || typeof agent !== 'object') return null;
  return agent.alias || 'custom';
}

/**
 * What the workspace picker should show for the workspace a cell HAS.
 *
 * A cell embeds a COPY of its workspace and keeps no reference to the palette entry it came from, so
 * the two drift apart the moment the palette is edited — an entry re-aliased, a folder corrected, a
 * cell created before either. The picker used to reverse-match by `work_folder` and fall back to
 * "— none —", which is a lie: the cell has a workspace and its next run will use it.
 *
 * Returns `{ alias }` when the palette still has it, `{ folder }` when it does not, and null when the
 * cell genuinely has no workspace — which the caller renders as "— none —", the one case that means it.
 */
export function workspaceSelection(workspace, workspaces) {
  const folder = workspace && workspace.work_folder;
  if (!folder) return null;
  const entry = (Array.isArray(workspaces) ? workspaces : [])
    .find(candidate => candidate && candidate.work_folder === folder);
  return entry && entry.alias ? { alias: entry.alias } : { folder };
}
